import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button, 
  Paper,
  Fade,
  Grow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  AlertTitle
} from '@mui/material';
import { 
  PrivacyTip,
  Security,
  Email,
  Phone,
  LocationOn,
  Lock,
  ExpandMore,
  CheckCircle,
  Info,
  CameraAlt,
  Fingerprint,
  Description,
  Gavel,
  Warning,
  Sync,
  ChildCare,
  Update,
  Delete,
  VerifiedUser,
  DataUsage,
  Home
} from '@mui/icons-material';

// Define color palette (same as Loans page)
const colors = {
  primary: '#132714',
  secondary: '#57a649',
  accent: '#f5a623',
  lightBg: '#f8fffb',
  textDark: '#132714',
  textLight: '#ffffff'
};

const PrivacyPolicy = () => {
  const permissionDetails = [
    {
      icon: <CameraAlt sx={{ fontSize: 32, color: colors.secondary }} />,
      title: "Camera Access",
      uses: [
        "Scanning QR codes for quick payments and account linking",
        "Capturing images of cheques for remote cheque deposit (where enabled)",
        "Photographing supporting documents for loan applications and KYC verification",
        "Scanning barcodes for bill payments",
        "Profile photo capture for identity verification"
      ],
      permissionType: "Foreground only — never accessed in background"
    },
    {
      icon: <LocationOn sx={{ fontSize: 32, color: colors.secondary }} />,
      title: "Location Services",
      uses: [
        "Locating the nearest Farmers Bank branch or ATM",
        "Fraud detection — verifying that a transaction originates from your usual geographic area",
        "Compliance with regulatory requirements for geo-tagged financial transactions",
        "Confirming your location during high-value or unusual transactions for added security"
      ],
      permissionType: "Foreground only — never accessed in background"
    }
  ];

  const dataCollectionTable = [
    { category: "Account Information", examples: "Name, account number, ID number, date of birth", purpose: "Authentication, account management" },
    { category: "Contact Details", examples: "Mobile number, email address, physical address", purpose: "Notifications, OTP delivery, correspondence" },
    { category: "Transaction Data", examples: "Payment history, amounts, recipients, timestamps", purpose: "Banking services, statements, fraud detection" },
    { category: "Device Information", examples: "Device ID, OS version, App version, IP address", purpose: "Security, troubleshooting, compatibility" },
    { category: "Camera Data", examples: "Captured images for cheques, documents, QR scans", purpose: "Service delivery only — not stored permanently" },
    { category: "Location Data", examples: "Approximate or precise GPS coordinates", purpose: "ATM/branch finder, fraud detection" },
    { category: "Biometric Data", examples: "Fingerprint or face recognition (on-device only)", purpose: "App authentication — never transmitted to servers" },
    { category: "Usage & Analytics", examples: "Features accessed, session duration, crash logs", purpose: "App improvement, performance monitoring" }
  ];

  const userRights = [
    { icon: <Description sx={{ color: colors.secondary }} />, right: "Right to Access", description: "Request a copy of the personal data we hold about you" },
    { icon: <CheckCircle sx={{ color: colors.secondary }} />, right: "Right to Rectification", description: "Request correction of inaccurate or incomplete data" },
    { icon: <Delete sx={{ color: colors.secondary }} />, right: "Right to Erasure", description: "Request deletion of data we are not legally required to retain" },
    { icon: <Gavel sx={{ color: colors.secondary }} />, right: "Right to Object", description: "Object to processing of your data for specific purposes" },
    { icon: <DataUsage sx={{ color: colors.secondary }} />, right: "Right to Portability", description: "Receive your data in a structured, machine-readable format" },
    { icon: <Sync sx={{ color: colors.secondary }} />, right: "Withdraw Consent", description: "Revoke permissions via your device settings at any time" }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: 'auto',
          minHeight: '450px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          py: 8,
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            opacity: 0.3
          }
        }}
      >
        <Container>
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <PrivacyTip sx={{ fontSize: 80, mb: 2, color: colors.accent }} />
              <Typography 
                variant="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 2,
                  lineHeight: 1.2
                }}
              >
                Privacy Policy
              </Typography>
              <Typography 
                variant="h6"
                sx={{ 
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 3,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 300,
                  opacity: 0.9
                }}
              >
                Mobile Banking Application
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Chip 
                  label="Effective Date: 1 June 2025" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '0.85rem'
                  }} 
                />
                <Chip 
                  label="Last Updated: 26 February 2026" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '0.85rem'
                  }} 
                />
              </Box>
              <Alert 
                icon={<Info />} 
                severity="info"
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  color: colors.primary,
                  '& .MuiAlert-icon': {
                    color: colors.secondary
                  }
                }}
              >
                <AlertTitle sx={{ fontWeight: 700, color: colors.primary }}>Important Notice</AlertTitle>
                This Privacy Policy applies to the Farmers Bank Mobile Banking Application available on Android via 
                Google Play. By downloading, installing, or using the App, you agree to the practices described in 
                this Policy. <strong>Farmers Bank does not sell, trade, or rent your personal information to third parties 
                for marketing purposes.</strong>
              </Alert>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 10 }}>
        {/* Overview Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: '16px',
            background: 'white',
            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
            border: `1px solid ${colors.secondary}20`,
            mb: 6
          }}
        >
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 700,
                  color: colors.primary,
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2rem' }
                }}
              >
                1. Overview
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7 }}>
                Farmers Bank is a regulated financial institution operating under the laws of the Kingdom of Eswatini. 
                We collect limited personal and device information solely to deliver secure, efficient, and personalised 
                banking services to our customers.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7 }}>
                This Policy covers all information collected through the App, including information obtained via device 
                permissions such as camera access and location services.
              </Typography>
            </Box>
          </Fade>
        </Paper>

        {/* Device Permissions Section */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700,
              color: colors.primary,
              mb: 4,
              fontSize: { xs: '1.8rem', md: '2rem' }
            }}
          >
            2. Device Permissions — Camera & Location
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, mb: 4 }}>
            The App may request access to your device's camera and location services. These permissions are requested 
            only when required for a specific feature and are never accessed in the background without your knowledge. 
            You may grant or revoke these permissions at any time through your device settings.
          </Typography>

          <Grid container spacing={4}>
            {permissionDetails.map((permission, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: `${colors.secondary}10`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        {permission.icon}
                      </Box>
                      <Typography variant="h4" sx={{ color: colors.primary, fontWeight: 600 }}>
                        {permission.title}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      {permission.uses.map((use, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          <CheckCircle sx={{ color: colors.secondary, fontSize: 20, mr: 1.5, mt: 0.3 }} />
                          <Typography variant="body2">{use}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Chip 
                      label={permission.permissionType} 
                      size="small"
                      sx={{ 
                        backgroundColor: `${colors.secondary}15`,
                        color: colors.secondary,
                        fontWeight: 600,
                        mt: 1
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>

          <Alert 
            severity="info" 
            sx={{ mt: 3, backgroundColor: `${colors.secondary}10`, color: colors.primary }}
          >
            <strong>Important:</strong> Permissions are requested at the point of use and can be denied without preventing 
            core banking functions such as viewing balances, making standard transfers, or viewing statements.
          </Alert>
        </Box>

        {/* Information We Collect Section */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700,
              color: colors.primary,
              mb: 4,
              fontSize: { xs: '1.8rem', md: '2rem' }
            }}
          >
            3. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, mb: 3 }}>
            Depending on how you use the App, we may collect the following categories of information:
          </Typography>

          <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ backgroundColor: colors.secondary }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Examples</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataCollectionTable.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.category}</TableCell>
                    <TableCell>{row.examples}</TableCell>
                    <TableCell>{row.purpose}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* How We Use Your Information */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: '16px',
            background: 'white',
            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
            border: `1px solid ${colors.secondary}20`,
            mb: 6
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, mb: 3, fontSize: { xs: '1.8rem', md: '2rem' } }}>
            4. How We Use Your Information
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.secondary, mb: 1 }}>Service Delivery</Typography>
            <Typography variant="body1">To process transactions, display account balances and statements, facilitate fund transfers, and enable all core banking features available on the App.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.secondary, mb: 1 }}>Security & Fraud Prevention</Typography>
            <Typography variant="body1">To detect unusual activity, verify your identity during login and high-value transactions, and protect your account from unauthorised access. Location and device data may be used to flag suspicious transactions that deviate from your normal behaviour patterns.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.secondary, mb: 1 }}>Regulatory Compliance</Typography>
            <Typography variant="body1">To comply with the Financial Services Regulatory Authority (FSRA) of Eswatini, Anti-Money Laundering (AML) obligations, Know Your Customer (KYC) requirements, and applicable data protection legislation.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.secondary, mb: 1 }}>Customer Support</Typography>
            <Typography variant="body1">To respond to queries, resolve disputes, and provide technical assistance related to your account or App usage.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.secondary, mb: 1 }}>Service Improvement</Typography>
            <Typography variant="body1">To analyse anonymous, aggregated usage patterns to improve App performance, usability, and feature offerings.</Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 3, backgroundColor: `${colors.accent}15` }}>
            <strong>We do not use your data for:</strong> Selling to advertisers, building marketing profiles for third parties, or any purpose not described in this Policy.
          </Alert>
        </Paper>

        {/* Information Sharing */}
        <Accordion sx={{ mb: 3, borderRadius: '16px !important', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.secondary }} />}>
            <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
              5. Information Sharing & Disclosure
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph><strong>Farmers Bank does not sell or rent your personal information.</strong> We may share data only in the following limited circumstances:</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.secondary }}>Regulatory & Legal Requirements</Typography>
              <Typography variant="body2">Where required by law, court order, or instruction from a regulatory body such as the FSRA, Central Bank of Eswatini, or law enforcement agencies acting with lawful authority.</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.secondary }}>Service Providers</Typography>
              <Typography variant="body2">We may engage trusted third-party service providers (e.g., cloud hosting, SMS gateways, payment processors) who process data on our behalf under strict data processing agreements that prohibit independent use of your data.</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.secondary }}>Interbank & Payment Networks</Typography>
              <Typography variant="body2">When you initiate inter-bank transfers or mobile money transactions, necessary transaction details are shared with the receiving institution or payment network to complete the transaction.</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.secondary }}>Business Transfers</Typography>
              <Typography variant="body2">In the event of a merger, acquisition, or transfer of business assets, your data may be transferred as part of that transaction, subject to equivalent privacy protections.</Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Data Retention */}
        <Accordion sx={{ mb: 3, borderRadius: '16px !important' }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.secondary }} />}>
            <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
              6. Data Retention
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We retain your personal data for as long as your account remains active and for a minimum period of <strong>seven (7) years after account closure</strong>, in compliance with financial record-keeping regulations in Eswatini.
            </Typography>
            <Typography variant="body1" paragraph>
              Camera images captured for cheque deposits or document scanning are processed and deleted within 30 days unless retention is required for audit or dispute resolution. Location data logs are retained for a maximum of 12 months for fraud analysis purposes, after which they are anonymised or deleted.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Security Measures */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #1a3a1c 100%)`,
            color: 'white',
            mb: 6
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1.8rem', md: '2rem' } }}>
            7. Security Measures
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.accent, mb: 1 }}>Encryption</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>All data transmitted between the App and our servers is encrypted using TLS 1.2 or higher. Sensitive data at rest is encrypted using AES-256 encryption standards.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.accent, mb: 1 }}>Authentication</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>The App supports multi-factor authentication (MFA), including One-Time Passwords (OTP) via SMS and on-device biometric authentication. Biometric data (fingerprint/face) is processed exclusively on your device using the Android Biometric API and is never transmitted to our servers.</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.accent, mb: 1 }}>Access Controls</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Access to customer data within our systems is restricted to authorised personnel on a need-to-know basis, with full audit logging of data access events.</Typography>
          </Box>

          <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}>
            <strong>Note:</strong> Despite these measures, no electronic transmission or storage system is 100% secure. If you suspect unauthorised access to your account, contact Farmers Bank immediately on our 24/7 banking helpline.
          </Alert>
        </Paper>

        {/* Your Rights */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, mb: 4, fontSize: { xs: '1.8rem', md: '2rem' } }}>
            8. Your Rights
          </Typography>
          <Grid container spacing={3}>
            {userRights.map((right, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{ p: 3, textAlign: 'center', height: '100%', borderRadius: '12px' }}>
                  <Box sx={{ fontSize: 40, mb: 1 }}>{right.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary, mb: 1 }}>{right.right}</Typography>
                  <Typography variant="body2" color="text.secondary">{right.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary', fontStyle: 'italic' }}>
            To exercise any of these rights, please contact us using the details in Section 11. We will respond within 30 days of receiving your request. Note that some requests may be limited by our legal obligations as a regulated financial institution.
          </Typography>
        </Box>

        {/* Children's Privacy */}
        <Accordion sx={{ mb: 3, borderRadius: '16px !important' }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.secondary }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChildCare sx={{ color: colors.secondary }} />
              <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
                9. Children's Privacy
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              The Farmers Bank Mobile App is intended for use by persons aged 18 years and over. We do not knowingly collect 
              personal information from children under 18. If you believe a minor has provided us with personal information, 
              please contact us immediately and we will take steps to delete that information.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Changes to Policy */}
        <Accordion sx={{ mb: 6, borderRadius: '16px !important' }}>
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.secondary }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Update sx={{ color: colors.secondary }} />
              <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
                10. Changes to This Policy
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, 
              or other factors. When we make material changes, we will notify you through the App and update the Last Updated date 
              at the top of this document.
            </Typography>
            <Typography variant="body1">
              Your continued use of the App after notification constitutes acceptance of the revised Policy. Previous versions of 
              this Policy are available upon request.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Contact Section */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, mb: 4, fontSize: { xs: '1.8rem', md: '2rem' } }}>
              11. Contact Us
            </Typography>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: '16px',
                backgroundColor: '#f8fffb',
                border: `1px solid ${colors.secondary}20`
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ color: colors.primary, fontWeight: 600, mb: 3 }}>
                Data Protection & Privacy Office
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1"><strong>Address:</strong> Farmers Bank Head Office, Mbabane, Kingdom of Eswatini</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1"><strong>Email:</strong> privacy@farmersbank.co.sz</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Phone sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1"><strong>Website:</strong> www.farmersbank.co.sz</Typography>
              </Box>
              <Alert severity="info" sx={{ backgroundColor: `${colors.secondary}10` }}>
                You may also lodge a complaint with the Financial Services Regulatory Authority (FSRA) of Eswatini if you are unsatisfied with our handling of your data.
              </Alert>
            </Paper>
          </Grid>
        </Grid>

        {/* Footer Note */}
        <Box sx={{ mt: 6, textAlign: 'center', pt: 4, borderTop: `1px solid ${colors.secondary}20` }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © 2026 Farmers Bank (Eswatini). All Rights Reserved. | Regulated by the FSRA of Eswatini
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;